/* eslint-disable @typescript-eslint/no-explicit-any */
// M10-15600: shared plumbing for the multi-GST counter forms.
//
// Every counter form (doctor / pharmacy / hospital, add and edit) builds its GST block from the
// same three pieces here: `gstDetailsSchema()` for validation, `toGstDetailsFormValue()` to seed
// the field array from an API response, and `toGstDetailsPayload()` to put it back on the wire.
// Keeping them together is what stops the eight forms from drifting apart again.
import * as yup from "yup";

import { GSTStatus } from "src/constants/counter-master";
import { checkCounterGstNumber } from "src/services/counter-api";

import { dedupeAsyncCheck } from "./utils";

export const GST_NUMBER_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export const ACTIVE_GST_STATUS = "Active";

// Rendered as a dropdown, sent as a plain boolean. The option ids are booleans so the value the
// form holds is already what the payload needs.
export const DefaultGSTOptions = [
  { name: "Yes", id: true },
  { name: "No", id: false },
];

//M10-15603
export const ValidGSTOptions = [
  { name: "Yes", id: "Y" },
  { name: "No", id: "N" },
];

export const VALID_GST_YES = ValidGSTOptions[0];
export const VALID_GST_NO = ValidGSTOptions[1]; //M10-15969
//M10-15603

export interface GstDetailFormRow {
  counter_gst_dtl_id?: number | null;
  gst_number: string;
  gst_status: { name: string; id: string } | null;
  is_default: { name: string; id: boolean } | null;
  valid_gst: { name: string; id: string } | null; //M10-15603
}

export const createEmptyGstRow = (isDefault = false): GstDetailFormRow => ({
  counter_gst_dtl_id: null,
  gst_number: "",
  gst_status: null,
  is_default: DefaultGSTOptions.find((option) => option.id === isDefault) || DefaultGSTOptions[1],
  valid_gst: VALID_GST_YES, //M10-15603
});

const toStatusOption = (status?: string | null) =>
  status ? GSTStatus.find((option) => option.id === status) || { name: status, id: status } : null;

const toDefaultOption = (isDefault?: boolean | string | null) =>
  DefaultGSTOptions.find((option) => option.id === (isDefault === true || isDefault === "true")) || DefaultGSTOptions[1];

//M10-15603
const toValidGstOption = (validGst?: string | null) =>
  validGst === "N" ? ValidGSTOptions[1] : VALID_GST_YES;
//M10-15603
/**
 * Seeds the field array from whatever the API gave us.
 *
 * Prefers `gst_details` (the multi-GST shape). Counters created before this feature shipped are
 * backfilled by the migration, but a stale cached response or a CREQ-imported draft can still
 * arrive carrying only the flat gst_number/gst_status — those fall back to a single default row so
 * the form opens populated instead of blank.
 */
export const toGstDetailsFormValue = (source: any): GstDetailFormRow[] => {
  const details = source?.gst_details;

  if (Array.isArray(details) && details.length) {
    return details.map((row: any) => ({
      counter_gst_dtl_id: row?.counter_gst_dtl_id ?? null,
      gst_number: row?.gst_number || "",
      gst_status: toStatusOption(row?.gst_status),
      is_default: toDefaultOption(row?.is_default),
      valid_gst: toValidGstOption(row?.valid_gst), //M10-15603
    }));
  }

  if (source?.gst_number) {
    return [
      {
        counter_gst_dtl_id: null,
        gst_number: source.gst_number,
        gst_status: toStatusOption(source?.gst_status),
        is_default: DefaultGSTOptions[0],
        valid_gst: VALID_GST_YES, //M10-15603
      },
    ];
  }

  return [];
};

/**
 * The wire shape. Only `gst_details` goes to the backend — it derives the header gst_number /
 * gst_status from the row flagged `is_default`, so sending them again would just be a second
 * source of truth that could disagree.
 */
export const toGstDetailsPayload = (rows: GstDetailFormRow[] | undefined, gstAvailable?: string) => {
  if (gstAvailable === "No" || !Array.isArray(rows)) return [];

  return rows
    .filter((row) => row?.gst_number)
    .map((row) => ({
      ...(row.counter_gst_dtl_id ? { counter_gst_dtl_id: row.counter_gst_dtl_id } : {}),
      gst_number: row.gst_number,
      gst_status: row.gst_status?.id || null,
      is_default: row.is_default?.id === true,
      // Only an Active GST can be valid; the default is always Active, hence always valid.
      valid_gst:
        row.is_default?.id === true
          ? VALID_GST_YES.id
          : row.gst_status?.id !== ACTIVE_GST_STATUS
            ? VALID_GST_NO.id
            : row.valid_gst?.id || VALID_GST_YES.id, //M10-15603
    }));
};

/** The default GST — what the counter header stores and every read-only screen displays. */
export const getDefaultGstRow = (rows: GstDetailFormRow[] | undefined) =>
  Array.isArray(rows) ? rows.find((row) => row?.is_default?.id === true) : undefined;

interface GstSchemaOptions {
  /** Excluded from the cross-counter duplicate check so a counter can re-save its own GST. */
  counterId?: number | string | null;
}

/**
 * Validation for the `gst_details` array.
 *
 * Row-level rules (format, status, duplicates, cross-counter uniqueness) are attached to the row
 * fields so the message lands on the offending row. The "exactly one Active default" rules are
 * array-level, since no single row is at fault when none or several are marked.
 */
export const gstDetailsSchema = (options: GstSchemaOptions = {}) =>
  yup
    .array()
    .of(
      yup.object().shape({
        gst_number: yup
          .string()
          .nullable()
          .test("gst-number", "GST number is required.", function (value: any) {
            if (!isGstRequired(this)) return true;

            if (!value) return this.createError({ message: "GST number is required." });

            if (!GST_NUMBER_REGEX.test(value)) return this.createError({ message: "Invalid GST format." });

            // The same GST twice on one counter is always wrong, chain pharmacy or not — that flag
            // only ever permitted sharing a GST *between* counters.
            const siblings: GstDetailFormRow[] = getSiblingRows(this);
            const occurrences = siblings.filter(
              (row) => (row?.gst_number || "").toUpperCase() === String(value).toUpperCase()
            );
            if (occurrences.length > 1) {
              return this.createError({ message: "This GST number is already added for this counter." });
            }

            return true;
          })
          .test("gst-number-unique", "Invalid GST Number", async function (value: any) {
            if (!isGstRequired(this) || !value || !GST_NUMBER_REGEX.test(value)) return true;

            // Chain pharmacy is toggled in the same form, so it is read from the live values
            // rather than passed in — that keeps the schema usable as a module-level constant.
            const chainPharmacy = !!getRootValues(this)?.chain_pharmacy;
            const counterId = options.counterId ?? "";

            try {
              const res = await dedupeAsyncCheck(`counter-gst:${value}:${counterId}:${chainPharmacy}`, () =>
                checkCounterGstNumber(value, { counterId, chainPharmacy })
              );
              if (res?.status !== 200) throw new Error();
              if (res?.data?.isUnique === false) {
                return this.createError({ message: res?.data?.message || "This GST number is already in use." });
              }
            } catch (error) {
              return this.createError({ message: "Unable to verify this GST number. Please try again." });
            }

            return true;
          }),
        gst_status: yup
          .object()
          .nullable()
          .test("gst-status", "GST status is required.", function (value: any) {
            if (!isGstRequired(this)) return true;
            return !!value;
          }),
        is_default: yup.object().nullable(),
        //M10-15603
        valid_gst: yup
          .object()
          .nullable()
          .test("valid-gst", "Valid GST is required.", function (value: any) {
            if (!isGstRequired(this)) return true;
            if (!value) return false;
            const status = (this.parent as GstDetailFormRow)?.gst_status?.id;
            if (value.id === VALID_GST_YES.id && status && status !== ACTIVE_GST_STATUS) {
              return this.createError({ message: "Only an Active GST can be marked as valid." });
            }
            return true;
          }),
          //M10-15603
      })
    )
    .test("gst-details-required", "At least one GST is required when GST is available.", function (value: any) {
      const gstAvailable = (this.parent as any)?.gst_available;
      if (gstAvailable?.id !== "Yes") return true;
      return Array.isArray(value) && value.some((row: GstDetailFormRow) => !!row?.gst_number);
    })
    .test("gst-details-default", "", function (value: any) {
      const gstAvailable = (this.parent as any)?.gst_available;
      if (gstAvailable?.id !== "Yes") return true;

      const rows: GstDetailFormRow[] = (value || []).filter((row: GstDetailFormRow) => !!row?.gst_number);
      if (!rows.length) return true;

      // No default is allowed — the counter then carries no GST on counter_hdr. Two is not, since
      // the header holds exactly one and there would be no way to say which.
      const defaults = rows.filter((row) => row?.is_default?.id === true);

      if (defaults.length > 1) {
        return this.createError({ message: "Only one GST can be the default GST." });
      }

      // Applies only once a row claims the default: that GST is the one counter_hdr carries, so it
      // has to be a live one. A list with no default is asked for nothing.
      if (defaults.length === 1 && defaults[0]?.gst_status?.id !== ACTIVE_GST_STATUS) {
        return this.createError({ message: "The default GST must have an Active GST status." });
      }

      return true;
    });

/**
 * `this.from` walks up out of the array item: [0] is the row itself, and the last entry is the
 * whole form values object — where `gst_available`, `chain_pharmacy` and the sibling rows live.
 */
const getRootValues = (context: any) => context?.from?.[context.from.length - 1]?.value;

/** Rows only need validating while GST is switched on. */
const isGstRequired = (context: any) => getRootValues(context)?.gst_available?.id === "Yes";

const getSiblingRows = (context: any): GstDetailFormRow[] => {
  const root = getRootValues(context);
  return Array.isArray(root?.gst_details) ? root.gst_details : [];
};
